import {
    useCallback,
    useMemo,
} from 'react'
import {
    useParams,
} from 'react-router-dom'

import {
    RequestKeys,
} from 'app/hooks/useRequest'
import useLabel from 'app/hooks/useLabel'
import {
    useGetRequestSpreadSheetAdapter,
} from 'app/shared-components/SkyNetSpreadSheet'
import useSimpleApiRequest from 'app/hooks/useSimpleApiRequest'
import StatusHandler from 'app/shared-components/StatusHandler'
import {
    AsyncMutationOptions,
} from 'app/types/request.types'
import useFilter from 'app/hooks/useFilter'
import {
    useSkyNetPagination, SkyNetPaginationContext,
} from 'app/shared-components/SkyNetPagination'
import requests from 'app/requests/APricingRateCard/APricingRateCard.requests'
import {
    StandardAdditionalFeeFilterRequestResponse,
} from 'app/requests/APricingRateCard/APricingRateCard.types'
import AdditionalFeesSpreadsheet from 'app/Apps/Rates/components/AdditionalFeesSpreadsheet'
import {
    APricingAdditionalFeesEditable,
    APricingAdditionalFeesTable,
} from 'app/Apps/Rates/RateCards/AdditionalFees/AdditionalFees.types'
import {
    prepareFeesToBulkEdit,
} from 'app/Apps/Rates/RateCards/OtherLaneFees/OtherLaneFees.utils'
import defaultRateCardsBulkSort from 'app/Apps/Rates/RateCards/RateCardsBulkEdit/RateCardsBulkEdit.utils'

import getAPricingAdditionalFeesConfig from './APricingStandardAdditionalFeesBulkEdit.config'

export default function APricingStandardAdditionalFeesBulkEdit({
    onClose,
}: Readonly<{
    onClose: () => void,
}>) {
    const l = useLabel()
    const {
        key,
    } = useParams()
    const rateCardId = Number(key)
    const filterOptions = useFilter<StandardAdditionalFeeFilterRequestResponse[]>()
    const paginationOptions = useSkyNetPagination(100)

    const config = useCallback(
        (params: Record<string, unknown>) => {
            return requests.standardAdditionalFeeFilterRequest({
                rateCardId,
                params,
            })
        },
        [rateCardId],
    )

    const {
        isError, loaded, error, refetch,
    } = useGetRequestSpreadSheetAdapter<StandardAdditionalFeeFilterRequestResponse>(
        {
            config,
            requestKey:
            RequestKeys.getAPricingRateCardAdditionalFeeRequest,
            enabled: true,
            filterOptions,
            paginationOptions,
            defaultSort: defaultRateCardsBulkSort,
        },
    )

    const {
        mutateAsync: bulkEdit,
    } = useSimpleApiRequest<{
        items: APricingAdditionalFeesEditable[]
    }>({
        name: 'APricingStandardAdditionalFeesBulkUpdate',
        title: l('STANDARD_ADDITIONAL_FEES_RATE_CARDS_UPDATED'),
        requestParams: requests.updateStandardAdditionalFeeRateCardBulk({
            rateCardId,
        }),
        onSuccess: refetch,
    })

    const onSave = useCallback(
        async (
            items: APricingAdditionalFeesTable[],
            options?: AsyncMutationOptions,
        ) => {
            await bulkEdit(
                {
                    items: prepareFeesToBulkEdit<
                        APricingAdditionalFeesTable, APricingAdditionalFeesEditable
                    >(items),
                },
                {
                    ...options,
                    onSuccess: options?.onSuccess || onClose,
                },
            )
        },
        [
            bulkEdit,
            onClose,
        ],
    )

    const filterConfigRequest = useMemo(() => {
        return ({
            fieldName,
        }: { fieldName: string }) => {
            return requests.standardAdditionalFeeFilterRequestCount({
                rateCardId,
                fieldName,
            })
        }
    }, [rateCardId])

    return (
        <StatusHandler
            isError={isError}
            isFetching={!loaded}
            error={error}
            isSuccess={loaded}
        >
            <SkyNetPaginationContext.Provider value={paginationOptions}>
                <AdditionalFeesSpreadsheet
                    filterOptions={filterOptions}
                    onCancel={onClose}
                    onSave={onSave}
                    filterConfigRequest={filterConfigRequest}
                    getConfig={getAPricingAdditionalFeesConfig}
                />
            </SkyNetPaginationContext.Provider>
        </StatusHandler>
    )
}
